import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Copy, Share2, Trash2, X, Plus } from "lucide-react";
import VenueMap from "@/components/VenueMap";
import { CATEGORIES } from "@/data/constants";

export default function TripDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [saved, setSaved] = useState([]);
  const [busy, setBusy] = useState(false);
  const [addVid, setAddVid] = useState("");

  const load = async () => {
    try {
      const [t, s] = await Promise.all([api.get(`/trips/${id}`), api.get("/saves")]);
      setTrip(t.data); setSaved(s.data);
    } catch {
      toast.error("Trip not found");
      navigate("/trips");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const patch = async (partial) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/trips/${id}`, partial);
      setTrip(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };

  const savePublic = async (val) => {
    await patch({ is_public: val });
    toast.success(val ? "Trip is now shareable" : "Trip is private");
  };

  const removeVenue = async (vid) => {
    try {
      const { data } = await api.delete(`/trips/${id}/venues/${vid}`);
      setTrip(data);
    } catch { toast.error("Could not remove"); }
  };

  const addVenue = async () => {
    if (!addVid) return;
    try {
      const { data } = await api.post(`/trips/${id}/venues/${addVid}`);
      setTrip(data);
      setAddVid("");
      toast.success("Added to trip");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add"); }
  };

  const removeTrip = async () => {
    if (!window.confirm("Delete this trip? This cannot be undone.")) return;
    try {
      await api.delete(`/trips/${id}`);
      toast.success("Trip deleted");
      navigate("/trips");
    } catch { toast.error("Could not delete"); }
  };

  const copyLink = () => {
    if (!trip?.share_slug) return;
    const url = `${window.location.origin}/trip/${trip.share_slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Share link copied");
  };

  if (!trip) return <div className="min-h-[50vh] flex items-center justify-center text-gray-500">Loading…</div>;

  const catName = (slug) => CATEGORIES.find(c => c.slug === slug)?.short || slug;
  const inTripIds = new Set((trip.venue_ids || []));
  const addOptions = saved.filter(v => !inTripIds.has(v.id));

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/trips" className="inline-flex items-center gap-1 text-sm text-gray-700 hover:text-blue-700"><ArrowLeft className="w-4 h-4"/>All trips</Link>

      <Card className="mt-4 border-2 border-gray-900 rounded-xl">
        <CardContent className="p-6 space-y-4">
          <div>
            <Label htmlFor="trip-name" className="text-sm text-gray-500">Trip name</Label>
            <Input id="trip-name" value={trip.name} onChange={e => setTrip({...trip, name: e.target.value})}
              onBlur={e => patch({ name: e.target.value })}
              className="mt-1 h-12 border-2 border-gray-300 focus:border-blue-600 font-heading text-2xl font-semibold" data-testid="trip-name-edit"/>
          </div>
          <div>
            <Label htmlFor="trip-desc" className="text-sm text-gray-500">Notes</Label>
            <Textarea id="trip-desc" rows={2} value={trip.description || ""}
              onChange={e => setTrip({...trip, description: e.target.value})}
              onBlur={e => patch({ description: e.target.value })}
              className="mt-1 border-2 border-gray-300 focus:border-blue-600"/>
          </div>
          <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-gray-200">
            <div className="flex items-center gap-2">
              <Switch checked={!!trip.is_public} onCheckedChange={savePublic} disabled={busy} id="public-toggle" data-testid="trip-public-toggle"/>
              <Label htmlFor="public-toggle" className="flex items-center gap-1"><Share2 className="w-4 h-4"/>Share publicly</Label>
            </div>
            {trip.is_public && trip.share_slug && (
              <Button variant="outline" size="sm" onClick={copyLink} className="rounded-full border-2 border-gray-900" data-testid="copy-share-link">
                <Copy className="w-4 h-4 mr-1"/>Copy link
              </Button>
            )}
            <div className="ml-auto">
              <Button variant="ghost" onClick={removeTrip} className="text-red-600 hover:bg-red-50" data-testid="delete-trip-btn">
                <Trash2 className="w-4 h-4 mr-1"/>Delete trip
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="mt-8">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-heading text-2xl font-semibold">Places in this trip <span className="text-gray-500 font-normal text-lg">({(trip.venues || []).length})</span></h2>
          <div className="flex items-center gap-2">
            <Select value={addVid} onValueChange={setAddVid}>
              <SelectTrigger className="h-10 min-w-[240px] border-2 border-gray-300" data-testid="add-from-saved-select">
                <SelectValue placeholder={addOptions.length ? "Add from your saved places" : "No more saved places to add"} />
              </SelectTrigger>
              <SelectContent>
                {addOptions.map(v => <SelectItem key={v.id} value={v.id}>{v.venue_name} · {v.area}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={addVenue} disabled={!addVid} className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="add-to-trip-btn"><Plus className="w-4 h-4 mr-1"/>Add</Button>
          </div>
        </div>

        {(trip.venues || []).length === 0 ? (
          <div className="mt-4 border-2 border-dashed border-gray-300 rounded-xl p-10 text-center">
            <p className="text-gray-700">No places yet. Save some venues first, then add them here.</p>
            <Button asChild variant="outline" className="mt-4 rounded-full border-2 border-gray-900"><Link to="/search">Browse venues</Link></Button>
          </div>
        ) : (
          <>
            <ul className="mt-4 space-y-3">
              {(trip.venues || []).map((v, i) => (
                <li key={v.id} className="flex items-start justify-between gap-3 bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-gray-900 text-white flex items-center justify-center text-sm font-bold">{i + 1}</div>
                    <div>
                      <Link to={`/venue/${v.id}`} className="font-heading font-semibold text-lg hover:underline">{v.venue_name}</Link>
                      <div className="text-sm text-gray-600">{catName(v.category)} · {v.area}{v.postcode ? ` · ${v.postcode}` : ""}</div>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => removeVenue(v.id)} aria-label="Remove from trip" data-testid={`remove-from-trip-${v.id}`}>
                    <X className="w-4 h-4"/>
                  </Button>
                </li>
              ))}
            </ul>
            {(trip.venues || []).some(v => typeof v.lat === "number") && (
              <div className="mt-8">
                <h3 className="font-heading text-xl font-semibold mb-3">Route map</h3>
                <VenueMap venues={trip.venues} height={380} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
